import React, { useEffect, useState } from "react";
import { Modal, ModalHeader } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updatePayment } from "common/services/payments.service";
import PaymentForm from "./PaymentForm";
import { toast } from "react-toastify";
import { extractFieldErrors } from "common/lib/fieldErrors";

const UpdatePayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
  selectedRecord: any;
}> = ({ toggle, isModalOpen, fetchData, selectedRecord }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<any>([]);
  // validation

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      projectOrFund: selectedRecord?.projectOrFund
        ? selectedRecord?.projectOrFund.trim()
        : null,
      donorOrFund: selectedRecord?.donorOrFund
        ? selectedRecord?.donorOrFund.trim()
        : null,
      staffId: selectedRecord?.staffId ? selectedRecord?.staffId.trim() : null,
      isSalaryAligned: selectedRecord?.isSalaryAligned,
      amountPerMonth: selectedRecord?.amountPerMonth,
      startDate:
        (selectedRecord?.startDate && new Date(selectedRecord?.startDate)) ||
        null,
      endDate:
        (selectedRecord?.endDate && new Date(selectedRecord?.endDate)) || null,
      paymentIdentifier: selectedRecord?.paymentIdentifier,
      dataProvidedBy: selectedRecord?.dataProvidedBy
        ? selectedRecord?.dataProvidedBy.trim()
        : null,
      positionTitle: selectedRecord?.positionTitle
        ? selectedRecord?.positionTitle.trim()
        : null,
      individual: {
        value: selectedRecord?.individual?.id,
        label: selectedRecord?.individual?.fullName,
      },
      mda: {
        value: selectedRecord?.mda?.id,
        label: selectedRecord?.mda?.name,
      },
      agency: {
        value: selectedRecord?.agency?.id,
        label: selectedRecord?.agency?.name,
      },
      positionType: {
        value: selectedRecord?.positionType?.id,
        label: selectedRecord?.positionType?.name,
      },
      paymentCategory: {
        value: selectedRecord?.paymentCategory?.id,
        label: selectedRecord?.paymentCategory?.name,
      },
      region: {
        value: selectedRecord?.mda?.region?.id,
        label: selectedRecord?.mda?.region?.name,
      },
      thematicArea: {
        value: selectedRecord?.thematicArea?.id,
        label: selectedRecord?.thematicArea?.name,
      },
      sector: {
        value: selectedRecord?.sector?.id,
        label: selectedRecord?.sector?.name,
      },
    },
    validationSchema: Yup.object({
      projectOrFund: Yup.string().required("Please enter project"),
      donorOrFund: Yup.string(),
      staffId: Yup.string().nullable(),
      amountPerMonth: Yup.number().required("Please enter amount per month"),
      startDate: Yup.string()
        .required("Please enter start date")
        .test(
          "is-before-end-date",
          "Start date must be before or equal to end date",
          function (value) {
            const { endDate } = this.parent;
            if (!value || !endDate) return true; // Skip validation if either date is empty

            const startDateObj = new Date(value);
            const endDateObj = new Date(endDate);

            return startDateObj <= endDateObj;
          }
        ),
      endDate: Yup.string().required("Please enter end date"),
      // paymentIdentifier: Yup.string().required(
      //   "Please enter payment identifier"
      // ),
      dataProvidedBy: Yup.string().nullable(),
      positionTitle: Yup.string().required("Please enter position title"),
      individual: Yup.object().shape({
        value: Yup.number().required("Please select individual"),
      }),
      mda: Yup.object().shape({
        value: Yup.number().required("Please select MDA"),
      }),
      agency: Yup.object().shape({
        value: Yup.number().required("Please select agency"),
      }),
      positionType: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      paymentCategory: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      thematicArea: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      sector: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
    }),
    onSubmit: async (values: any) => {
      const data = {
        id: selectedRecord?.id,
        data: {
          individual: values.individual?.value || null,
          mda: values.mda?.value || null,
          agency: values.agency?.value || null,
          positionType: values.positionType?.value || null,
          paymentCategory: values.paymentCategory?.value || null,
          projectOrFund: values.projectOrFund,
          donorOrFund: values.donorOrFund,
          staffId: values.staffId,
          isSalaryAligned: values.isSalaryAligned,
          amountPerMonth: values.amountPerMonth,
          startDate:
            (values.startDate &&
              values.startDate.toLocaleDateString("en-CA")) ||
            null,
          endDate:
            (values.endDate && values.endDate.toLocaleDateString("en-CA")) ||
            null,
          // paymentIdentifier: values.paymentIdentifier,
          dataProvidedBy: values.dataProvidedBy,
          positionTitle: values.positionTitle,
          thematicArea: values.thematicArea?.value || null,
          sector: values.sector?.value || null,
        },
      };

      setLoading(true);
      try {
        await updatePayment(selectedRecord?.id, data);
        fetchData();
        validation.resetForm();
        toggle();
        toast.success("Payment updated successfully", {});
      } catch (e: any) {
        const errors = extractFieldErrors(e?.response);
        setFieldErrors(errors);
        toast.error("Payment update failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  // Update fieldErrors when validation errors change
  useEffect(() => {
    const validationErrors = [];

    // Convert Formik validation errors to fieldErrors format
    for (const [fieldName, errorMessage] of Object.entries(validation.errors)) {
      if (validation.touched[fieldName] && errorMessage) {
        // Handle nested object errors (like individual.value, mda.value, etc.)
        if (typeof errorMessage === "object" && errorMessage !== null) {
          for (const [nestedField, nestedError] of Object.entries(
            errorMessage
          )) {
            if (nestedError) {
              validationErrors.push({
                field: `${fieldName}.${nestedField}`,
                message: nestedError,
              });
            }
          }
        } else {
          validationErrors.push({
            field: fieldName,
            message: errorMessage,
          });
        }
      }
    }

    setFieldErrors(validationErrors);
  }, [validation.errors, validation.touched]);

  return (
    <Modal
      id="showModal"
      size="xl"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Update Payment
      </ModalHeader>

      <PaymentForm
        validation={validation}
        isUpdate
        loading={loading}
        toggle={toggle}
        fieldErrors={fieldErrors}
      />
    </Modal>
  );
};

export default UpdatePayment;
